import React, { useMemo, useState, useRef, SyntheticEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Typography, ConfirmDialog, CircularProgress } from '@occc/ui-design-system';
import { useOpenCommerceUserUpdateMutation } from '@occc-app/dataAccessLayer/openCommerceUser/mutations';
import { useGetOpenCommerceUserByUuid } from '@occc-app/dataAccessLayer/openCommerceUser/queries';
import { getPersonFullName } from '@occc-app/lib/person';
import { Open_Commerce_User_Status } from '@occc/graphql';

import { UpdateUserPage } from './UpdateUserPage';

import { UserFormInitialValues, UserFormValues } from '../types';
import { userRoleMap } from '../constants';
import { routeCreator } from '../utils';

export const UpdateUserPageContainer = () => {
  const { userUuid = '' } = useParams<{ userUuid: string }>();
  const [isConfirmationDialogOpened, setConfirmationDialogOpened] = useState(false);
  const [isEnableConfirmationOpen, setEnableConfirmationOpen] = React.useState<boolean>(false);

  const pendingFormValuesRef = useRef<UserFormValues | null>(null);

  const navigate = useNavigate();
  const { loading, error, data } = useGetOpenCommerceUserByUuid(userUuid);

  const initialValues: UserFormInitialValues = useMemo(() => {
    return {
      firstName: data?.name.first,
      lastName: data?.name.last,
      userRole: data?.userRole,
      email: data?.email,
    };
  }, [data]);

  const userDisplayName = useMemo(() => {
    const userFullName = getPersonFullName(data?.name);
    const userDisplayNameOrEmail = userFullName || data?.email || '';

    return userDisplayNameOrEmail;
  }, [data?.name, data?.email]);

  const onCancel = React.useCallback(() => {
    navigate(routeCreator.list());
  }, [navigate]);

  const onUserUpdateCompleted = () => navigate(-1);

  const [userUpdateMutation, { loading: updateLoading }] = useOpenCommerceUserUpdateMutation({
    onCompleted: onUserUpdateCompleted,
    refetchQueries: ['openCommerceUsers'],
  });

  const onEnableButtonClick = React.useCallback(() => {
    setEnableConfirmationOpen(true);
  }, []);

  const onCloseButtonClick = React.useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    setEnableConfirmationOpen(false);
  }, []);

  if (error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  if (loading && !data) {
    return <CircularProgress />;
  }

  if (!data) {
    return <Typography color='error'>No data. Something went wrong. Please try again.</Typography>;
  }

  const onUserEnableDialogConfirm = (event: SyntheticEvent) => {
    event.stopPropagation();

    userUpdateMutation({
      variables: {
        input: {
          uuid: data.uuid,
          status: Open_Commerce_User_Status.Enabled,
        },
      },
    });

    setEnableConfirmationOpen(false);
  };

  const updateUser = (input: UserFormValues) => {
    userUpdateMutation({
      variables: {
        input: {
          uuid: data.uuid,
          name: {
            first: input.firstName,
            last: input.lastName,
          },
          email: input.email,
          userRole: input.userRole,
        },
      },
    });
  };

  const onUserRoleUpdateDialogConfirm = () => {
    if (pendingFormValuesRef.current) {
      updateUser(pendingFormValuesRef.current);
    }

    setConfirmationDialogOpened(false);
  };

  const onSubmit = (values: UserFormValues) => {
    if (data.userRole !== values.userRole) {
      pendingFormValuesRef.current = values;
      setConfirmationDialogOpened(true);
      return Promise.resolve();
    } else {
      return updateUser(values);
    }
  };

  const renderEnableConfirmationDialog = () => {
    return (
      <ConfirmDialog
        dialogTitle='Enable User'
        open={isEnableConfirmationOpen}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onUserEnableDialogConfirm,
        }}
      >
        <Typography>
          {`Are you sure you would like to enable "${userDisplayName}"? This will restore their access to Command Center.`}
        </Typography>
      </ConfirmDialog>
    );
  };

  const currentUserRole = pendingFormValuesRef.current?.userRole
    ? userRoleMap[pendingFormValuesRef.current?.userRole]
    : '';

  return (
    <>
      <UpdateUserPage
        onSubmit={onSubmit}
        initialValues={initialValues}
        loading={loading || updateLoading}
        user={data}
        onEnable={onEnableButtonClick}
        onCancel={onCancel}
      />
      <ConfirmDialog
        dialogTitle='Change User Access Role'
        open={isConfirmationDialogOpened}
        onClose={() => setConfirmationDialogOpened(false)}
        confirmButtonText='Update'
        confirmButtonProps={{
          onClick: onUserRoleUpdateDialogConfirm,
        }}
      >
        <Typography>
          Are you sure you want to change this user to {currentUserRole}? This may cause access
          issues for this user.
        </Typography>
      </ConfirmDialog>

      {renderEnableConfirmationDialog()}
    </>
  );
};
